import { Component, OnDestroy } from '@angular/core';
import { FirebaseError } from 'firebase/app';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { IconName } from 'ngx-bootstrap-icons';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionLike } from 'rxjs';
import {
  Invoice,
  InvoiceStatus,
} from 'src/app/data/dietitian/domain/subscription/invoice';
import { Subscription } from 'src/app/data/dietitian/domain/subscription/subscription';
import { ConfirmationModalComponent } from 'src/app/ui/components/confirmation-modal/confirmation-modal.component';

import { AppService } from '../../../../../../app.service';
import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { CodedFeature } from '../../../../../../data/dietitian/domain/subscription/coded_feature';
import { Feature } from '../../../../../../data/dietitian/domain/subscription/feature';
import { PaymentMethod } from '../../../../../../data/dietitian/domain/subscription/payment_method';
import { Premium } from '../../../../../../data/dietitian/domain/subscription/premium';
import { SubscriptionFeature } from '../../../../../../data/dietitian/domain/subscription/subscription_feature';
import {
  StripePrice,
  StripeProduct,
} from '../../../../../../data/dietitian/repositories/subscription_repository';
import { IncomingMessageEventProvider } from '../../../../../../data/notification/incoming-message-event-provider';
import { AccountSubscriptionFacade } from './account-subscription.facade';
import { FeatureByCodePopupComponent } from './components/feature-by-code-popup/feature-by-code-popup.component';
import { FeatureSubscriptionConfirmationPopupComponent } from './components/feature-subscription-confirmation-popup/feature-subscription-confirmation-popup.component';

export interface CodedFeatureModel {
  feature: CodedFeature;
  subscription: SubscriptionFeature | undefined;
}

export interface CodedFeatureModel {
  feature: CodedFeature;
  subscription: SubscriptionFeature | undefined;
}

export interface CodedFeatureModel {
  feature: CodedFeature;
  subscription: SubscriptionFeature | undefined;
}

export interface FeatureModel {
  feature: Feature;
  isLoading: boolean;
  subscription: SubscriptionFeature | undefined;
  stripeProduct: StripeProduct | undefined;
}

export interface SubscriptionModel {
  subscription: Subscription | undefined;
  prices: StripePrice[];
}

export interface StripeBalanceModel {
  id: string;
  date: Date;
  type: string;
  amount: number;
  currency: string;
  description: string | null;
  debitCredit: number;
  invoice: Invoice | null;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-account-subscription',
  templateUrl: './account-subscription.component.html',
  styleUrls: ['./account-subscription.component.scss'],
  providers: [AccountSubscriptionFacade],
})
export class AccountSubscriptionComponent implements OnDestroy {
  loadingSubscription = true;
  loadingInvoices = true;
  loadingPaymentMethods = true;
  loadingBalances = true;
  loadingFeatures = true;
  loadingSubscriptionAction = false;
  loadingUpdatePaymentMethod = false;
  errorSubscription?: string;
  errorFeatures?: string;
  errorInvoices?: string;
  errorPaymentMethods?: string | undefined;
  errorBalances?: string | undefined;
  errorSubscriptionAction?: string;
  latestSubscription: SubscriptionModel | undefined;
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
  balances: StripeBalanceModel[];
  bsModalRef?: BsModalRef;
  featureModels?: FeatureModel[];
  codedFeatureModels?: CodedFeatureModel[];
  currentTabName = 'subscriptions';
  dietitian: Dietitian | undefined;
  changeSubscriptionInProgress = false;
  cancelSubscriptionInProgress = false;
  private incomingMessageEventSubscription: SubscriptionLike;
  private dietitianSubscription: SubscriptionLike;

  get now(): Date {
    return new Date();
  }

  constructor(
    private facade: AccountSubscriptionFacade,
    private appService: AppService,
    private modalService: BsModalService,
    private toastrService: ToastrService,
    incomingMessageEventProvider: IncomingMessageEventProvider,
  ) {
    this.dietitianSubscription = this.facade.dietitianState$.subscribe(
      (event) => {
        this.dietitian = event.entity;
        this.loadSubscription(this.dietitian).then(() => {
          if (this.latestSubscription?.subscription?.status === 'past_due') {
            this.loadInvoices().then();
          }
        });
        this.loadFeatures(this.dietitian).then();
      },
    );

    this.incomingMessageEventSubscription =
      incomingMessageEventProvider.events$.subscribe((event) => {
        appService.logger.debug(
          '🎃 AccountSubscriptionComponent.incomingMessageProvider',
          event,
        );
        if (
          event &&
          event.entity.props &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionId') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionStatus') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('productId') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionType')
        ) {
          if (event.entity.props['subscriptionType'] === 'premium') {
            this.loadSubscription(this.facade.dietitian).then(() => {
              this.loadingSubscription = false;
              this.toastrService.success('Abonnement Premium souscrit');
            });
            if (event.entity.props['subscriptionStatus'] === 'past_due') {
              this.loadInvoices().then();
            }
          }
          if (event.entity.props['subscriptionType'] === 'feature') {
            this.loadFeatures(this.facade.dietitian).then(() => {
              this.loadingSubscription = false;
              this.toastrService.success('Abonnement Option souscrit');
            });
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.incomingMessageEventSubscription?.unsubscribe();
    this.dietitianSubscription?.unsubscribe();
  }

  public async modifyPaymentInformation() {
    this.loadingUpdatePaymentMethod = true;
    this.errorSubscriptionAction = undefined;
    try {
      window.location.href =
        await this.facade.createUpdatePaymentMethodCheckoutSession(
          window.location.href,
          window.location.href,
        );
    } catch (error) {
      if (error instanceof Error) {
        this.errorSubscriptionAction = error.message;
      }
    } finally {
      this.loadingUpdatePaymentMethod = false;
    }
  }

  async loadSubscription(dietitian: Dietitian | undefined) {
    if (dietitian) {
      this.loadingSubscription = true;
      try {
        this.latestSubscription = await this.facade.geLatestSubscription(
          dietitian,
        );
      } catch (err: any) {
        this.errorSubscription = err;
      } finally {
        this.loadingSubscription = false;
      }
    }
  }

  async loadFeatures(dietitian: Dietitian | undefined) {
    if (dietitian) {
      try {
        this.featureModels = (await this.facade.getFeatures()).map((f) => {
          return {
            feature: f,
            isLoading: true,
          } as FeatureModel;
        });

        for (const model of this.featureModels) {
          model.stripeProduct = await this.facade.getStripeProduct(
            model.feature.stripeProductId.id.toString(),
          );

          this.facade
            .getRunningSubscriptionFeature(dietitian, model.feature)
            .then((sub) => {
              model.subscription = sub;
              model.isLoading = false;
            });
        }
      } catch (err: any) {
        this.errorFeatures = err;
      } finally {
        this.loadingFeatures = false;
      }
    }
  }

  async loadInvoices() {
    this.loadingInvoices = true;
    try {
      const res = await this.facade.getInvoices(10, null, null);
      this.invoices = res.results.filter(
        (invoice) =>
          invoice.status === InvoiceStatus.Paid ||
          invoice.status === InvoiceStatus.Open,
      );
    } catch (err: any) {
      this.errorInvoices = err;
    } finally {
      this.loadingInvoices = false;
    }
  }

  async loadBalances() {
    this.loadingInvoices = true;
    try {
      this.balances = await this.facade.getBalances();
    } catch (err: any) {
      this.errorBalances = err;
    } finally {
      this.loadingBalances = false;
    }
  }

  async loadPaymentMethods() {
    this.loadingPaymentMethods = true;
    try {
      this.paymentMethods = await this.facade.getPaymentMethods();
    } catch (err: any) {
      this.errorPaymentMethods = err;
    } finally {
      this.loadingPaymentMethods = false;
    }
  }

  async subscribe(premium: Premium) {
    this.loadingSubscriptionAction = true;
    this.errorSubscriptionAction = undefined;
    try {
      window.location.href =
        await this.facade.createSubscriptionCheckoutSession(
          premium.stripePriceId.id.toString(),
          window.location.href,
          window.location.href,
        );
    } catch (error) {
      if (error instanceof Error) {
        this.errorSubscriptionAction = error.message;
      }
    }
    // this.loadingSubscriptionAction = false;
  }

  confirmUnsubscribe() {
    let title = 'Annuler votre abonnement Premium ?';
    let subtitle = 'Voulez-vous vraiment annuler votre abonnement Premium ?';
    if (
      this.featureModels &&
      this.featureModels.length > 0 &&
      this.hasSubscriptions(this.featureModels)
    ) {
      title = 'Annuler votre abonnement Premium et vos options ?';
      subtitle =
        'Attention, vos options seront également annulées ! Voulez-vous vraiment annuler votre abonnement Premium et vos options ?';
    }
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title,
        subtitle,
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(() => {
        if (this.dietitian) {
          this.cancelSubscriptionInProgress = true;
          this.unsubscribe(this.dietitian)
            .then()
            .finally(() => (this.cancelSubscriptionInProgress = false));
        }
      });
    }
  }

  async unsubscribe(dietitian: Dietitian) {
    this.loadingSubscriptionAction = true;
    this.errorSubscriptionAction = undefined;
    try {
      await this.facade.cancelSubscriptions();
    } catch (error) {
      if (error instanceof Error) {
        this.errorSubscriptionAction = error.message;
      }
    }
    this.loadingSubscriptionAction = false;

    await this.loadSubscription(dietitian);
  }

  public get isAlreadySubscribed(): boolean {
    return (
      !!this.latestSubscription &&
      !!this.latestSubscription.subscription &&
      this.latestSubscription.subscription.isActive
    );
  }

  public get isTrialing(): boolean {
    return (
      !!this.latestSubscription &&
      !!this.latestSubscription.subscription &&
      this.latestSubscription.subscription.isTrialing
    );
  }

  public get isIssuedPayment(): boolean {
    return (
      !!this.latestSubscription &&
      !!this.latestSubscription.subscription &&
      (this.latestSubscription.subscription.isUnpaid ||
        this.latestSubscription.subscription.isPastDue)
    );
  }

  public get endPeriodFormattedDate() {
    return moment(this.latestSubscription?.subscription?.periodEnd).format(
      'DD MMMM yyyy à HH:mm',
    );
  }

  public downloadPdf(pdfUrl: string) {
    window.open(pdfUrl);
  }

  async payInvoice() {
    this.loadingInvoices = true;
    const subscription = await this.facade.getLastInvoiceBySubscription(
      this.latestSubscription?.subscription?.stripeSubscriptionId?.id.toString(),
    );
    if (
      subscription &&
      subscription['hosted_invoice_url'] &&
      typeof subscription['hosted_invoice_url'] === 'string'
    ) {
      window.open(subscription['hosted_invoice_url'], '_blank');
    } else {
      this.toastrService.info(
        "La régularisation n'est pas possible. Veuillez contacter MonSuiviDiet sur contact@monsuividiet.com.",
      );
    }
  }

  isActive(tabName: string): boolean {
    return this.currentTabName === tabName;
  }

  async onTab(tabName: string) {
    this.currentTabName = tabName;
    if (this.currentTabName === 'invoices') {
      await this.loadInvoices().then();
    }

    if (this.currentTabName === 'card') {
      await this.loadPaymentMethods().then();
      await this.loadBalances().then();
    }
  }

  getPrice(stripeProduct: StripeProduct | undefined) {
    if (
      stripeProduct &&
      stripeProduct.prices &&
      stripeProduct.prices.length > 0
    ) {
      const amount = (Number(stripeProduct.prices[0].amount) / 100).toFixed(2);
      return (
        'Tarif : ' +
        amount +
        (stripeProduct.prices[0].currency === 'eur'
          ? '€'
          : stripeProduct.prices[0].currency) +
        this.getPeriod(stripeProduct.prices[0])
      );
    }
    return '';
  }

  getPeriod(price: StripePrice): string {
    switch (price.period) {
      case 'month_1':
      default:
        return ' / mois';
    }
  }

  getIcon(icon: string | undefined): IconName {
    if (icon) {
      return icon as IconName;
    }
    return 'stars';
  }

  async subscribeFeature(model: FeatureModel) {
    if (!model.isLoading) {
      model.isLoading = true;
      if (model.stripeProduct) {
        this.loadingSubscriptionAction = true;
        this.errorSubscriptionAction = undefined;
        const initialState: ModalOptions = {
          class: 'modal-lg modal-dialog-centered',
          initialState: { model },
        };
        this.bsModalRef = this.modalService.show(
          FeatureSubscriptionConfirmationPopupComponent,
          initialState,
        );
        if (
          this.bsModalRef.content instanceof
          FeatureSubscriptionConfirmationPopupComponent
        ) {
          this.bsModalRef.content.validate.subscribe(async (coupon) => {
            try {
              await this.facade.subscribeFeature(
                model?.stripeProduct?.prices[0].id,
                coupon,
              );
              setTimeout(() => {
                if (model.isLoading) {
                  location.reload();
                }
              }, 6000);
            } catch (error) {
              if (error instanceof Error || error instanceof FirebaseError) {
                this.errorSubscriptionAction = error.message;
                if (this.errorSubscriptionAction === 'PMD-UNREACHABLE') {
                  this.toastrService.error(
                    'Veuillez indiquer au préalable un mode de paiement par défaut (Depuis l\'onglet "Votre mode de paiement".',
                    'Erreur Mode de paiement !',
                    { closeButton: true, disableTimeOut: true },
                  );
                }
              }
              model.isLoading = false;
            } finally {
              this.loadingSubscriptionAction = false;
            }
          });
          this.bsModalRef.content.cancel.subscribe(() => {
            model.isLoading = false;
            this.loadingSubscriptionAction = false;
          });
        }
      }
    }
  }

  formattedDate(date: Date) {
    return moment(date).format('DD MMMM yyyy à HH:mm');
  }

  unsubscribeFeature(model: FeatureModel) {
    model.isLoading = true;
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Annuler votre abonnement ' + model.stripeProduct?.name + ' ?',
        subtitle:
          'Voulez-vous vraiment annuler votre abonnement ' +
          model.stripeProduct?.name +
          ' ?',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(async () => {
        this.loadingSubscriptionAction = true;
        this.errorSubscriptionAction = undefined;
        try {
          if (model.subscription) {
            await this.facade.cancelSubscriptionFeature(model.subscription);
          }
        } catch (error) {
          if (error instanceof Error) {
            this.errorSubscriptionAction = error.message;
          }
        }
        this.loadingSubscriptionAction = false;

        if (this.dietitian) {
          await this.loadFeatures(this.dietitian);
        }
      });
    }
  }

  private hasSubscriptions(featureModels: FeatureModel[]) {
    return (
      featureModels.map((m) => m.subscription).filter((s) => s?.isCancellable)
        .length > 0
    );
  }

  getIconPremium(prices: StripePrice[]): IconName {
    if (prices.length === 1) {
      switch (prices[0].period) {
        case 'year_1':
          return 'star-fill';
        case 'month_1':
        default:
          return 'star-half';
      }
    }
    return 'star';
  }

  getSubscriptionPeriod(prices: StripePrice[]) {
    if (prices.length === 1) {
      switch (prices[0].period) {
        case 'year_1':
          return 'Annuel';
        case 'month_1':
        default:
          return 'Mensuel';
      }
    }
    return '';
  }

  isMonthly(prices: StripePrice[]) {
    if (prices.length === 1) {
      if (prices[0].period === 'month_1') {
        return true;
      }
    }
    return false;
  }

  async updatePremium(
    subscriptionModel: SubscriptionModel,
    isMonthly: boolean,
  ) {
    if (isMonthly) {
      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          title: 'Confirmation de changement de formule',
          subtitle:
            "Attention, un paiement complémentaire sera prélevé sur votre carte bancaire correspondant au tarif de l'abonnement annuel (468€), auquel sera retiré le montant consommé de votre mois en cours.",
        },
      };
      this.bsModalRef = this.modalService.show(
        ConfirmationModalComponent,
        initialState,
      );
      if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
        this.bsModalRef.content.validate.subscribe(async () => {
          if (
            subscriptionModel.subscription &&
            subscriptionModel.subscription.stripeSubscriptionId
          ) {
            this.changeSubscriptionInProgress = true;
            this.loadingSubscriptionAction = true;
            await this.facade.updatePremium(
              subscriptionModel.subscription.stripeSubscriptionId.id.toString(),
              subscriptionModel.prices[0].id,
            );
            this.loadingSubscriptionAction = false;
          }
        });
      }
    }
  }

  async defaultPaymentMethod(paymentMethod: PaymentMethod) {
    this.loadingUpdatePaymentMethod = true;
    this.errorSubscriptionAction = undefined;
    try {
      await this.facade.defaultPaymentMethod(paymentMethod.id);
      this.loadPaymentMethods().then();
    } catch (error) {
      if (error instanceof Error) {
        this.errorSubscriptionAction = error.message;
      }
    } finally {
      this.loadingUpdatePaymentMethod = false;
    }
  }

  async completeSubscribeFeature(model: FeatureModel) {
    model.isLoading = true;
    this.loadingSubscriptionAction = true;
    if (model && model.subscription) {
      const subscription = await this.facade.loadFeature(
        model.subscription.id.toString(),
      );
      this.facade
        .subscriptionFeatureValueChanges(subscription.id.toString())
        .subscribe((returnedSubscription) => {
          if (returnedSubscription) {
            const expectedSubscriptionFeature = this.featureModels?.filter(
              (v) =>
                v.subscription &&
                v.subscription.id.toString() ===
                  returnedSubscription.id.toString(),
            );

            if (
              expectedSubscriptionFeature &&
              expectedSubscriptionFeature.length > 0 &&
              expectedSubscriptionFeature[0].subscription
            ) {
              expectedSubscriptionFeature[0].subscription.status =
                returnedSubscription.status;
              if (
                returnedSubscription.status != 'incomplete' &&
                returnedSubscription.status != 'past_due'
              ) {
                model.isLoading = false;
                this.loadingSubscriptionAction = false;
              }
            }
          }
        });

      const stripeInvoice = await this.facade.getLastInvoiceBySubscription(
        subscription.stripeSubscriptionId?.id.toString(),
      );

      if (
        stripeInvoice['hosted_invoice_url'] &&
        typeof stripeInvoice['hosted_invoice_url'] === 'string'
      ) {
        window.open(stripeInvoice['hosted_invoice_url'], '_blank');
      } else {
        this.toastrService.info(
          "L'action requise n'a pas pu être identifiée. Veuillez contacter MonSuiviDiet sur contact@monsuividiet.com.",
        );
      }
    } else {
      model.isLoading = false;
      this.loadingSubscriptionAction = false;
    }
  }

  subscribeCodedFeature(model: CodedFeatureModel) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        codedFeature: model.feature,
      },
    };
    this.bsModalRef = this.modalService.show(
      FeatureByCodePopupComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof FeatureByCodePopupComponent) {
      this.bsModalRef.content.onValidate.subscribe(async () => {
        // todo
      });
    }
  }
}
